import type {
  GuidanceTooltipSchemaType,
  Modification,
} from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { RuleDeletionAlert } from '../RuleDeletionAlert';
import { RulesContent, RulesManagerFilters } from '../RulesManager';
import {
  type Filters,
  type Modifiable,
  useModificationsFilter,
} from '../hooks';
import type { GuidanceManagerProps } from './GuidanceManager';
import { GuidanceTooltipsManagerSection } from './GuidanceTooltipsManagerSection';
import { GuidanceTooltipsRulePreview } from './GuidanceTooltipsRulePreview';

export type GuidanceManagerContentProps = Pick<
  GuidanceManagerProps,
  | 'pageUrl'
  | 'guidanceTooltipsModifications'
  | 'deleteGuidanceTooltipModification'
  | 'toggleGuidanceTooltipModificationVisibility'
  | 'onItemHover'
  | 'onItemUnhover'
  | 'onItemClick'
  | 'onSetGlobal'
>;

export const GuidanceManagerContent = ({
  guidanceTooltipsModifications,
  pageUrl,
  deleteGuidanceTooltipModification,
  toggleGuidanceTooltipModificationVisibility,
  onItemHover,
  onItemUnhover,
  onItemClick,
  onSetGlobal,
}: GuidanceManagerContentProps) => {
  const {
    searchValue,
    onSearchChange,
    selectedFilter,
    onFiltersChange,
    result: modificationsByUrl,
  } = useModificationsFilter({
    modifications: guidanceTooltipsModifications as Modifiable[],
    pageUrl,
  });

  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();

  const [ruleToDelete, setRuleToDelete] =
    useState<Modification<GuidanceTooltipSchemaType.guidanceTooltip> | null>(
      null
    );

  return (
    <>
      <RulesContent>
        <RulesManagerFilters<Filters>
          onSearchChange={onSearchChange}
          onFiltersChange={onFiltersChange}
          dropdownItems={[
            { id: 'all', label: 'All pages' },
            { id: 'current', label: 'Current page' },
          ]}
          searchValue={searchValue}
          selectedFilter={selectedFilter}
        />

        {Object.entries(modificationsByUrl).map(([url, modifications]) => {
          return (
            <GuidanceTooltipsManagerSection
              key={url}
              url={url}
              guidanceTooltipModifications={
                modifications as Modification<GuidanceTooltipSchemaType.guidanceTooltip>[]
              }
              onDelete={(ruleItem) => {
                setRuleToDelete(ruleItem);
                setShowDeletionAlert.setTrue();
              }}
              onToggleVisibility={async (ruleItem, isVisible) => {
                await toggleGuidanceTooltipModificationVisibility(
                  ruleItem,
                  isVisible
                );
              }}
              onItemHover={onItemHover}
              onItemUnhover={onItemUnhover}
              pageUrl={pageUrl}
              onItemClick={onItemClick}
              onSetGlobal={onSetGlobal}
            />
          );
        })}
      </RulesContent>
      <RuleDeletionAlert
        title="Delete tooltip"
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete"
        onConfirm={async () => {
          await deleteGuidanceTooltipModification(ruleToDelete);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={<GuidanceTooltipsRulePreview rule={ruleToDelete} />}
      />
    </>
  );
};
